import {default as React, useEffect, useState} from 'react';
import {NavLink} from 'react-router-dom';
import {Breadcrumbs} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {Trash2} from "lucide";
import {LucideIcon} from "~/components/LucideIcon";
import {useConfirm, useMessage} from '~/components/Dialogs';
import {onSubmitError} from '~/components/Form';
import {useModals} from '~/components/Modal';
import {Progress} from '~/components/Progress';
import {TableRowActions, TableSort, TableSortDirection} from '~/components/Table';
import {Title} from '~/components/Title';
import {useAbility} from '~/lib/ability';
import {useDataSource} from '~/lib/dataSource';
import {Intent} from '~/lib/intent';
import {DealsAbility} from '~/modules/Pmp/ability';
import {dealsAdapter} from '~/modules/Pmp/adapters';
import {deleteDeal} from '~/modules/Pmp/api';
import {DealForm} from '~/modules/Pmp/forms/DealForm';
import {defaultDeal} from '~/modules/Pmp/models/Deal';
import {Input} from '~/components/Controls/Input';
import {renderBoolean, renderToggle} from '~/lib/helpers';
import {sortByBoolean, sortByNumber, sortByString} from '~/lib/sort';
import style from './Pmp.module.scss';
import {PmpNavigation} from '~/modules/Pmp/components/PmpNavigation';
import {clientsLinks} from '~/modules/Clients';
import {biddersLinks} from '~/modules/Bidders';
import {pmpLinks} from '~/modules/Pmp';

const normalizeId = (value: any): number | null => {
    if (typeof value === 'number') return value;
    if (value && typeof value === 'object' && 'value' in value) {
        const numeric = Number(value.value);
        return Number.isNaN(numeric) ? null : numeric;
    }
    return null;
};

const normalizeIdArray = (values: any): number[] => {
    if (!Array.isArray(values)) return [];
    return values
        .map(item => normalizeId(item))
        .filter((id): id is number => typeof id === 'number');
};

export const DealsView = () => {
    const ability = useAbility(DealsAbility);
    let {state, fetchRecords, createRecord, updateRecord} = useDataSource(dealsAdapter);
    useEffect(() => fetchRecords(), []);

    const modals = useModals();
    const confirm = useConfirm();
    const message = useMessage();

    const createDeal = modals.create(DealForm, {
        onSubmit: (newDeal, formikActions, close) => {
            createRecord(newDeal).then(close).catch(onSubmitError(formikActions));
        }
    });

    const editDeal = modals.create(DealForm, {
        onSubmit: (updatedDeal, formikActions, close) => {
            updateRecord(updatedDeal).then(close).catch(onSubmitError(formikActions));
        }
    });

    const [filter, setFilter] = useState({
        search: ''
    });

    const setSearchFilter = (value: string) => {
        setFilter({...filter, search: value});
    };

    const recordsFilter = (record: any) => {
        if (!filter.search) {
            return record;
        }

        if (record.id.toLowerCase().includes(filter.search.toLowerCase())) {
            return record;
        }

        if (record.name.toLowerCase().includes(filter.search.toLowerCase())) {
            return record;
        }

        if (record.agencyName.toLowerCase().includes(filter.search.toLowerCase())) {
            return record;
        }

        return;
    };

    return <div>
        <div className="page-header-sticky">
            <div className="page-header-breadcrumbs">
                <Breadcrumbs />
            </div>
            <Title className={`${style.dealsList} page-header-title`} toolbar={<>
            {ability.canCreate() && <Button intent={Intent.PRIMARY}
                responsive
                disabled={state.isPending}
                onClick={() => createDeal.show({deal: defaultDeal})}
                leftIcon={<i className='fa fa-plus'/>}
            >Add deal</Button>}
            </>}>Deals</Title>
        </div>
        <PmpNavigation/>
        <div className="layout-content-box">
            <div>
                <form className={style.filter} onSubmit={(e) => e.preventDefault()}>
                    <Input
                        value={filter.search}
                        onChange={setSearchFilter}
                        placeholder='Search'
                        className={style.searchInput}
                        rightIcon={<i className="fa fa-search input-search-icon" />}
                    />
                </form>
            </div>
        <TableSort
            records={state.records.filter(recordsFilter)}
            defaultSort={{
                sort: sortByNumber,
                field: 'id',
                direction: TableSortDirection.DESC
            }}
            header
            columns={[
                {
                    header: 'ID',
                    field: 'id',
                    sort: sortByString,
                    cell: deal =><div className={style.ellipsisMulti}>{deal.id}</div>,
                }, {
                    header: 'Name',
                    field: 'name',
                    sort: sortByString,
                    cell: deal =><div className={style.ellipsisMulti}>{deal.name}</div>,
                },  {
                    header: 'Agency',
                    field: 'agencyId',
                    sort: sortByNumber,
                    cell: deal => <NavLink to={clientsLinks.agency(deal.agencyId)}>{deal.agencyName}</NavLink>
                }, {
                    header: 'Bidders ID',
                    field: 'bidderIds',
                    sort: sortByNumber,
                    cell: deal => {
                        const bidderIds = normalizeIdArray(deal.bidderIds);
                        return <div className={style.links}>{bidderIds.map(id =>
                            <span key={id} className={style.link}>
                                <NavLink to={biddersLinks.bidder(id)}>{id}</NavLink>
                            </span>
                        )}</div>;
                    }
                },  {
                    header: 'Supply Package',
                    field: 'supplyPackageId',
                    sort: sortByNumber,
                    cell: deal => {
                        const supplyPackageId = normalizeId(deal.supplyPackageId);
                        return <NavLink to={pmpLinks.supplyPackage(supplyPackageId)} className={style.ellipsisMulti}>
                            {deal.supplyPackageName}
                        </NavLink>;
                    }
                },  {
                    header: 'Active',
                    field: 'isActive',
                    cell: deal => ability.canEdit() ?
                        renderToggle(deal.isActive, value =>
                            confirm(deal.isActive ? 'Deactivate deal?' : 'Activate deal?', {
                                onOk: () => updateRecord({
                                    ...deal,
                                    isActive: value
                                }).catch(() => message('Can\'t activate deal.', {
                                    intent: Intent.DANGER,
                                    icon: <i className='fa fa-times'/>
                                }
                                )), icon: null, intent: Intent.WARNING
                            })) : renderBoolean(deal.isActive),
                    sort: sortByBoolean
                }, {
                    small: true,
                    cell: (deal) => {
                        const editButton = ability.canEdit() && (
                            <Button
                                small
                                variant="menu"
                                leftIcon={<i className='fa fa-edit'/>}
                                onClick={() => editDeal.show({deal})}
                            >
                                Edit
                            </Button>
                        );
                        const deleteButton = ability.canDelete() && (
                            <Button
                                small
                                variant="menu"
                                intent={Intent.DANGER}
                                leftIcon={<i className='fa fa-trash'/>}
                                onClick={() => confirm('Delete Supply Package?', {
                                    onOk: () => deleteDeal(deal.id).then(fetchRecords),
                                    icon: <LucideIcon icon={Trash2} size={20} />,
                                    intent: Intent.DANGER
                                })}
                            >
                                Delete
                            </Button>
                        );
                        return <TableRowActions>{editButton}{deleteButton}</TableRowActions>;
                    }
                }
            ]}
            placeholder={state.isPending && <Progress/>}/>
        </div>
    </div>;
};
